
'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Bookmark, BookmarkCheck, Clock, MapPin, ExternalLink } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import type { NewsArticle } from '@/lib/mockData';
import { getTimeAgo, getCategoryBySlug } from '@/lib/mockData';

interface NewsCardProps {
  article: NewsArticle;
  lang?: 'hi' | 'en';
  variant?: 'default' | 'hero' | 'compact';
  onBookmarkToggle?: (id: string) => void;
  isBookmarked?: boolean;
}

export default function NewsCard({
  article,
  lang = 'hi',
  variant = 'default',
  onBookmarkToggle,
  isBookmarked = false,
}: NewsCardProps) {
  const [imgError, setImgError] = useState(false);
  const hasImage = Boolean(article.image?.trim()) && !imgError;
  const category = getCategoryBySlug(article.category);

  const title = lang === 'hi' ? article.titleHindi : article.title;
  const summary = lang === 'hi' ? article.summaryHindi : article.summary;
  const timeAgo = getTimeAgo(article.publishedAt, lang);

  if (variant === 'hero') {
    return (
      <div className="relative rounded-2xl overflow-hidden bg-foreground group news-card-hover cursor-pointer h-72 sm:h-96">
        {hasImage ? (
          <AppImage
            src={article.image}
            alt={article.imageAlt || title}
            fill
            sizes="(max-width: 768px) 100vw, 60vw"
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            priority
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-800 via-slate-900 to-black text-5xl">
            📰
          </div>
        )}
        <div className="hero-gradient absolute inset-0" />

        {/* Top badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {article.isBreaking && (
            <span className="breaking-badge">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
              {lang === 'hi' ? 'ब्रेकिंग' : 'BREAKING'}
            </span>
          )}
          {article.isSampleData && (
            <span className="sample-data-badge">
              {lang === 'hi' ? 'सैंपल डेटा' : 'SAMPLE'}
            </span>
          )}
        </div>

        {/* Bookmark */}
        <button
          onClick={(e) => {
            e.preventDefault();
            onBookmarkToggle?.(article.id);
          }}
          className="absolute top-3 right-3 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors btn-press"
          aria-label="Bookmark article"
        >
          {isBookmarked ? (
            <BookmarkCheck size={16} className="text-yellow-400" />
          ) : (
            <Bookmark size={16} className="text-white" />
          )}
        </button>

        {/* Content */}
        <Link
          href={`/news-detail-screen?id=${article.id}`}
          className="absolute bottom-0 left-0 right-0 p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            {category && (
              <span
                className="text-white text-[10px] font-semibold px-2 py-0.5 rounded font-hindi"
                style={{ backgroundColor: category.color }}
              >
                {lang === 'hi' ? category.labelHindi : category.labelEnglish}
              </span>
            )}
            {article.districtHindi && lang === 'hi' && (
              <span className="text-white/70 text-[10px] flex items-center gap-1">
                <MapPin size={9} />
                {article.districtHindi}
              </span>
            )}
          </div>
          <h2 className="text-white font-hindi font-bold text-base sm:text-xl leading-snug line-clamp-3 mb-2">
            {title}
          </h2>
          <div className="flex items-center gap-3 text-white/70 text-xs">
            <span className="font-medium">{article.source}</span>
            <span className="flex items-center gap-1">
              <Clock size={10} />
              {timeAgo}
            </span>
          </div>
        </Link>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        href={`/news-detail-screen?id=${article.id}`}
        className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted transition-colors duration-150 group"
      >
        <div className="relative w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-muted">
          {hasImage ? (
            <AppImage
              src={article.image}
              alt={article.imageAlt || title}
              fill
              sizes="64px"
              className="object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl">
              📰
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-hindi font-semibold text-foreground line-clamp-2 leading-snug group-hover:text-primary transition-colors">
            {title}
          </p>
          <div className="flex items-center gap-2 mt-1 text-muted-foreground text-[10px]">
            <span>{article.source}</span>
            <span>•</span>
            <span>{timeAgo}</span>
          </div>
        </div>
      </Link>
    );
  }

  // Default card
  return (
    <div className="bg-card rounded-2xl overflow-hidden border border-border news-card-hover flex flex-col">
      {/* Image */}
      <div className="relative h-44 bg-muted overflow-hidden">
        {hasImage ? (
          <AppImage
            src={article.image}
            alt={article.imageAlt || title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover hover:scale-105 transition-transform duration-500"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl bg-muted">
            📰
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {article.isBreaking && (
            <span className="breaking-badge text-[9px] py-0.5">
              <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
              {lang === 'hi' ? 'ब्रेकिंग' : 'BREAKING'}
            </span>
          )}
          {article.isSampleData && (
            <span className="sample-data-badge text-[9px]">
              {lang === 'hi' ? 'सैंपल' : 'SAMPLE'}
            </span>
          )}
        </div>

        {/* Bookmark */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onBookmarkToggle?.(article.id);
          }}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors btn-press"
          aria-label="Bookmark"
        >
          {isBookmarked ? (
            <BookmarkCheck size={13} className="text-yellow-400" />
          ) : (
            <Bookmark size={13} className="text-white" />
          )}
        </button>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-1">
        {/* Category + District */}
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {category && (
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full font-hindi"
              style={{ color: category.color, backgroundColor: category.bgColor }}
            >
              {lang === 'hi' ? category.labelHindi : category.labelEnglish}
            </span>
          )}
          {article.districtHindi && lang === 'hi' && article.district !== article.category && (
            <span className="text-muted-foreground text-[10px] flex items-center gap-0.5">
              <MapPin size={9} />
              {article.districtHindi}
            </span>
          )}
        </div>

        {/* Title */}
        <Link href={`/news-detail-screen?id=${article.id}`}>
          <h3 className="font-hindi font-semibold text-sm text-card-foreground leading-snug line-clamp-3 hover:text-primary transition-colors cursor-pointer mb-2">
            {title}
          </h3>
        </Link>

        {/* Summary */}
        <p className="text-muted-foreground text-xs font-hindi leading-relaxed line-clamp-2 mb-3 flex-1">
          {summary}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-foreground">
              {article.source}
            </span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="text-[10px] flex items-center gap-1">
              <Clock size={10} />
              {timeAgo}
            </span>
            <a
              href={article.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1 hover:text-primary transition-colors btn-press"
              aria-label="Read original"
            >
              <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
